import { memo, useCallback, useMemo } from "react";
import { useWatch, type Path, type UseFormReturn } from "react-hook-form";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import { RentalType } from "@/shared/types/enums";
import { type WizardStepDef } from "@/features/rentals/components/rental-create/wizard/WizardStepper";
import { WizardShell } from "@/features/rentals/components/rental-create/wizard/WizardShell";
import { useWizardNav } from "@/features/rentals/components/rental-create/wizard/useWizardNav";
import { RentalSummaryPanel } from "@/features/rentals/components/rental-create/wizard/RentalSummaryPanel";
import { StepType } from "@/features/rentals/components/rental-create/wizard/steps/StepType";
import { StepItems } from "@/features/rentals/components/rental-create/wizard/steps/StepItems";
import { StepPayments } from "@/features/rentals/components/rental-create/wizard/steps/StepPayments";
import { StepReview } from "@/features/rentals/components/rental-create/wizard/steps/StepReview";
import type { RentalCreateFormSchema } from "@/features/rentals/components/rental-create/rental-create-schema";
import type { NewClientData } from "../../schemas";
import type { ContractOption, ClientOption } from "../wizard/ContractSelector";
import { formatAdminPeriodLabel } from "../../utils/rental-admin-payload";
import { StepAdminClientContract } from "./StepAdminClientContract";
import { StepAdminPeriod } from "./StepAdminPeriod";

type FieldName = Path<RentalCreateFormSchema>;

interface RentalAdminCreateWizardProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  stockItems: StockAvailabilityResponseDTO[];
  sellerLabel: string | null;
  selectedContractLabel: string;
  selectedClientLabel: string | null;
  isSubmitting: boolean;
  onTypeChange: (type: RentalType.SUIT | RentalType.GOWN) => void;
  onSellerChange: (sellerId: number | null, sellerName: string | null) => void;
  onContractSelect: (
    id: number,
    display: string,
    client: { id: number; name: string },
  ) => void;
  onContractClear: () => void;
  searchContracts: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ContractOption[]>;
  createContract: (
    contractNumber: string,
    clientId: number,
    signal?: AbortSignal,
  ) => Promise<{ id: number; contractNumber: string }>;
  searchClients: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<ClientOption[]>;
  createClient: (
    data: NewClientData,
    signal?: AbortSignal,
  ) => Promise<{ id: number; name: string }>;
  onStockAdd: (item: StockAvailabilityResponseDTO) => void;
  onStockRemove: (stockId: number) => void;
  onSubmit: () => void;
}

export const RentalAdminCreateWizard = memo(function RentalAdminCreateWizard({
  form,
  stockItems,
  sellerLabel,
  selectedContractLabel,
  selectedClientLabel,
  isSubmitting,
  onTypeChange,
  onSellerChange,
  onContractSelect,
  onContractClear,
  searchContracts,
  createContract,
  searchClients,
  createClient,
  onStockAdd,
  onStockRemove,
  onSubmit,
}: RentalAdminCreateWizardProps) {
  const rentalType = useWatch({ control: form.control, name: "rentalType" });
  const hasShoes = useWatch({ control: form.control, name: "hasShoes" });

  /**
   * A etapa de itens (3) é opcional. As datas efetivas do retroativo são
   * validadas junto com o período (2), onde o campo mora.
   */
  const getStepFields = useCallback(
    (index: number): FieldName[] => {
      switch (index) {
        case 0:
          return ["sellerId"];
        case 1:
          return ["selectedContractId", "selectedClientId"];
        case 2:
          return ["expectedOutAt", "expectedInAt", "outAt", "inAt"];
        case 3:
          return rentalType === RentalType.SUIT && hasShoes
            ? ["shoesPairQuantity"]
            : [];
        case 4:
          return ["totalValue", "description", "invoiceObservation"];
        default:
          return [];
      }
    },
    [rentalType, hasShoes],
  );

  const steps = useMemo<WizardStepDef[]>(
    () => [
      { key: "type", label: "Tipo" },
      { key: "client", label: "Cliente" },
      { key: "period", label: "Período" },
      {
        key: "items",
        label: rentalType === RentalType.SUIT ? "Peças" : "Itens",
      },
      { key: "payment", label: "Pagamento" },
      { key: "review", label: "Revisão" },
    ],
    [rentalType],
  );

  const { step, maxReached, goNext, goBack, goTo } = useWizardNav({
    form,
    steps,
    getStepFields,
  });

  return (
    <WizardShell
      form={form}
      steps={steps}
      step={step}
      maxReached={maxReached}
      isSubmitting={isSubmitting}
      onStepClick={goTo}
      onBack={goBack}
      onNext={goNext}
      onSubmit={onSubmit}
      summary={
        <RentalSummaryPanel
          form={form}
          sellerLabel={sellerLabel}
          clientLabel={selectedClientLabel}
          contractLabel={selectedContractLabel || null}
          formatPeriodLabel={formatAdminPeriodLabel}
        />
      }
    >
      {step === 0 ? (
        <StepType
          form={form}
          onTypeChange={onTypeChange}
          onSellerChange={onSellerChange}
        />
      ) : null}
      {step === 1 ? (
        <StepAdminClientContract
          form={form}
          selectedContractLabel={selectedContractLabel}
          onContractSelect={onContractSelect}
          onContractClear={onContractClear}
          searchContracts={searchContracts}
          createContract={createContract}
          searchClients={searchClients}
          createClient={createClient}
        />
      ) : null}
      {step === 2 ? <StepAdminPeriod form={form} /> : null}
      {step === 3 ? (
        <StepItems
          form={form}
          stockItems={stockItems}
          onStockAdd={onStockAdd}
          onStockRemove={onStockRemove}
        />
      ) : null}
      {step === 4 ? <StepPayments form={form} /> : null}
      {step === 5 ? (
        <StepReview
          form={form}
          sellerLabel={sellerLabel}
          selectedClientLabel={selectedClientLabel}
          selectedContractLabel={selectedContractLabel || null}
          parentContractLabel={null}
          onEditStep={goTo}
          formatPeriodLabel={formatAdminPeriodLabel}
          showParentContract={false}
          showRetroactive
        />
      ) : null}
    </WizardShell>
  );
});
